import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Stack,
  Chip,
} from '@mui/material';
import API_CONFIG from '../../config/api.config';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import { useSnackbar } from 'notistack';
import companySettingsService from '../../services/companySettingsService';
import { getAuthHeader, updateCompanyInfo } from '../../services/authService';
import axiosInstance from '../../utils/axios';

const PAGE_BG = '#f4f6f8';
const PANEL_BORDER = '1px solid #e5e7eb';

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: '0.8125rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 1.5,
};

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`company-settings-tabpanel-${index}`}>
      {value === index ? children : null}
    </div>
  );
}

const CompanySettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: '',
    gst: '',
    stateCode: '',
    addressLine1: '',
    addressLine2: '',
    contact: '',
    email: '',
    websitePath: '',
    orderPrefix: '',
    orderResetFrequency: 'MONTHLY',
    skuPrefix: '',
    skuPattern: '001',
    logoUrl: '',
    dayCloseEnabled: false,
    enableBatchWise: false,
    stockProfitCostMethod: 'FIFO'
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [suggestedPath, setSuggestedPath] = useState('');
  const [websitePathError, setWebsitePathError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactError, setContactError] = useState('');
  const [planFeatures, setPlanFeatures] = useState(null);
  const [gstStates, setGstStates] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_CONFIG.ENDPOINTS.STATES);
        if (!cancelled && Array.isArray(res.data)) {
          setGstStates(res.data);
        }
      } catch (e) {
        console.error('Failed to load GST states', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Helper function to get full logo URL
  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return '';
    // If logoUrl already starts with /api, construct full URL
    if (logoUrl.startsWith('/api')) {
      const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
      return `${baseUrl}${logoUrl}`;
    }
    // If it's already a full URL, return as is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    // Otherwise, assume it's a relative path and construct URL
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    return `${baseUrl}/api${logoUrl.startsWith('/') ? logoUrl : '/' + logoUrl}`;
  };

  // Reactively track current subscription plan features (for website domain visibility)
  useEffect(() => {
    // Read any features that Navigation has already loaded
    if (typeof window !== 'undefined' && window.__PLAN_FEATURES__) {
      setPlanFeatures(window.__PLAN_FEATURES__);
    }

    const handlePlanFeaturesUpdated = (event) => {
      const nextFeatures =
        event && event.detail
          ? event.detail
          : (typeof window !== 'undefined' && window.__PLAN_FEATURES__) || null;
      setPlanFeatures(nextFeatures);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
      }
    };
  }, []);

  // Check for changes
  useEffect(() => {
    const changed = Object.keys(settings).some(key =>
      settings[key] !== originalSettings[key]
    );
    setHasChanges(changed);
  }, [settings, originalSettings]);

  // Auto-suggest website path when company name changes (only if websitePath is empty)
  useEffect(() => {
    const generateSuggestedPath = async () => {
      if (settings.name && !settings.websitePath) {
        try {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/companies/settings/website-path/suggest?companyName=${encodeURIComponent(settings.name)}`,
            { headers: getAuthHeader() }
          );
          if (response.ok) {
            const data = await response.json();
            setSuggestedPath(data.suggestedPath || '');
          }
        } catch (error) {
          console.error('Error generating suggested path:', error);
          // Fallback: generate simple suggestion client-side
          const simple = settings.name.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '') || 'mywebsite';
          setSuggestedPath(simple);
        }
      } else if (!settings.name) {
        setSuggestedPath('');
      }
    };

    generateSuggestedPath();
  }, [settings.name, settings.websitePath]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await companySettingsService.getSettings();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      enqueueSnackbar('Failed to load company settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    if (field === 'contact') {
      value = value.replace(/\D/g, '').slice(0, 15);
    }
    if (field === 'email') {
      // no transform, only validate
    }
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear website path error when user types
    if (field === 'websitePath') {
      setWebsitePathError('');
    }
    // Basic immediate validations
    if (field === 'email') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      setEmailError(value && !pattern.test(value) ? 'Invalid email format' : '');
    }
    if (field === 'contact') {
      setContactError(value && value.replace(/\D/g, '').length === 0 ? 'Phone must contain digits' : '');
    }
  };

  const handleToggleChange = (field) => (event) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Validate email/phone
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (settings.email && !pattern.test(settings.email)) {
        setEmailError('Invalid email format');
        setSaving(false);
        return;
      }
      if (settings.contact) {
        const digits = settings.contact.replace(/\D/g, '');
        if (!digits || digits.length > 15) {
          setContactError(!digits ? 'Phone must contain digits' : 'Phone must be at most 15 digits');
          setSaving(false);
          return;
        }
      }

      // Validate website path if provided
      if (settings.websitePath && settings.websitePath.trim()) {
        const cleanPath = settings.websitePath.trim().toLowerCase();
        try {
          const checkResponse = await fetch(
            `${API_CONFIG.BASE_URL}/companies/settings/website-path/check?websitePath=${encodeURIComponent(cleanPath)}`,
            { headers: getAuthHeader() }
          );
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            if (!checkData.available) {
              setWebsitePathError('This domain is already taken. Please choose a different one.');
              setSaving(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking website path:', error);
          // Continue with save even if check fails (backend will validate)
        }
      }

      const response = await companySettingsService.updateSettings(settings);

      if (response.success) {
        setOriginalSettings(settings);

        // Sync with localStorage for top bar display
        updateCompanyInfo({ name: settings.name, logoUrl: settings.logoUrl });

        enqueueSnackbar(response.message, { variant: 'success' });

        if (response.note) {
          enqueueSnackbar(response.note, {
            variant: 'info',
            autoHideDuration: 8000
          });
        }
      } else {
        enqueueSnackbar(response.message || 'Failed to update settings', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to save company settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please select an image file', { variant: 'error' });
        return;
      }

      // Validate file size (3MB limit)
      if (file.size > 3 * 1024 * 1024) {
        enqueueSnackbar('File size must be less than 3MB', { variant: 'error' });
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      enqueueSnackbar('Please select a file to upload', { variant: 'error' });
      return;
    }

    try {
      setUploadingLogo(true);
      const response = await companySettingsService.uploadLogo(logoFile);

      if (response.success) {
        setSettings(prev => ({
          ...prev,
          logoUrl: response.logoUrl
        }));

        // Update localStorage for top bar
        updateCompanyInfo({ logoUrl: response.logoUrl });

        setLogoFile(null);
        setLogoPreview(null);
        enqueueSnackbar(response.message, { variant: 'success' });
      } else {
        enqueueSnackbar(response.message || 'Failed to upload logo', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to upload logo', { variant: 'error' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({
      ...prev,
      logoUrl: ''
    }));

    // Update localStorage for top bar
    updateCompanyInfo({ logoUrl: '' });

    setLogoFile(null);
    setLogoPreview(null);
  };

  // Website domain should only be visible when ecommerce website feature is enabled.
  // For legacy plans or while loading flags, we keep the legacy behaviour (visible).
  // Normalize value: only boolean true or string "true" counts as enabled (API/DB may return string "false").
  const ecommerceEnabled = planFeatures && (
    planFeatures.ecommerce_website === true ||
    (typeof planFeatures.ecommerce_website === 'string' && planFeatures.ecommerce_website.toLowerCase() === 'true')
  );
  const shouldShowWebsiteDomain =
    !planFeatures ||
    planFeatures._treatAllEnabled ||
    !!ecommerceEnabled;

  if (loading) {
    return (
      <Layout>
        <PageTitle title="Company Settings" />
        <Box sx={{ bgcolor: PAGE_BG, minHeight: '70vh' }}>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
              <CircularProgress />
            </Box>
          </Container>
        </Box>
      </Layout>
    );
  }

  const actionBar = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      {hasChanges && (
        <Chip size="small" label="Unsaved" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
      )}
      <Button variant="outlined" size="small" onClick={handleReset} disabled={!hasChanges || saving}>
        Reset
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={!hasChanges || saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Stack>
  );

  return (
    <Layout>
      <PageTitle title="Company Settings" />
      <Box sx={{ bgcolor: PAGE_BG, pb: 4 }}>
        <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: PANEL_BORDER,
              borderRadius: 2.5,
              overflow: 'hidden',
              bgcolor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderBottom: PANEL_BORDER,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BusinessIcon sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 }}>
                  Company settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  One save updates all sections.
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>{actionBar}</Box>
            </Box>

            <Box sx={{ px: { xs: 1, sm: 0 } }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Company settings sections"
                sx={{
                  px: { xs: 1, sm: 2 },
                  borderBottom: PANEL_BORDER,
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    py: 1.25,
                    px: 2,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: 'text.secondary',
                    '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
                  },
                  '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                }}
              >
                <Tab label="Company & contact" id="company-settings-tab-0" aria-controls="company-settings-tabpanel-0" />
                <Tab label="Orders & SKUs" id="company-settings-tab-1" aria-controls="company-settings-tabpanel-1" />
                <Tab label="Stock & features" id="company-settings-tab-2" aria-controls="company-settings-tabpanel-2" />
              </Tabs>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
              <TabPanel value={activeTab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Company name"
                      value={settings.name || ''}
                      onChange={handleInputChange('name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="GST number"
                      value={settings.gst || ''}
                      onChange={handleInputChange('gst')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address line 1"
                      value={settings.addressLine1 || ''}
                      onChange={handleInputChange('addressLine1')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address line 2"
                      value={settings.addressLine2 || ''}
                      onChange={handleInputChange('addressLine2')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="company-state-label">State (GST)</InputLabel>
                      <Select
                        labelId="company-state-label"
                        label="State (GST)"
                        value={settings.stateCode ?? ''}
                        onChange={handleInputChange('stateCode')}
                      >
                        <MenuItem value="">
                          <em>Not selected</em>
                        </MenuItem>
                        {gstStates.map((s) => (
                          <MenuItem key={s.stateCode} value={s.stateCode}>
                            {s.name} ({s.stateCode})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Contact number"
                      value={settings.contact || ''}
                      onChange={handleInputChange('contact')}
                      error={!!contactError}
                      helperText={contactError || undefined}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={settings.email || ''}
                      onChange={handleInputChange('email')}
                      error={!!emailError}
                      helperText={emailError || undefined}
                    />
                  </Grid>
                  {shouldShowWebsiteDomain && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Website domain"
                        value={settings.websitePath || ''}
                        onChange={handleInputChange('websitePath')}
                        error={!!websitePathError}
                        placeholder={suggestedPath || 'mywebsite'}
                        helperText={websitePathError || 'Lowercase, no spaces'}
                        inputProps={{
                          pattern: '[a-z0-9-]+',
                          style: { textTransform: 'lowercase' },
                        }}
                        onInput={(e) => {
                          e.target.value = e.target.value.toLowerCase().replace(/\s+/g, '');
                        }}
                        onFocus={() => {
                          if (!settings.websitePath && suggestedPath) {
                            setSettings((prev) => ({
                              ...prev,
                              websitePath: suggestedPath,
                            }));
                          }
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        border: '1px dashed #cbd5e1',
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          alignSelf: { xs: 'center', sm: 'flex-start' },
                        }}
                      >
                        <Avatar
                          src={logoPreview || getLogoUrl(settings.logoUrl) || ''}
                          variant="rounded"
                          sx={{
                            width: 96,
                            height: 96,
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            '& img': { objectFit: 'contain' },
                          }}
                        >
                          <ImageOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                        </Avatar>
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {settings.logoUrl || logoFile ? 'Your company logo' : 'Add your company logo'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                          Shown on invoices, receipts, and the app header. PNG or JPG, max 3MB.
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            type="file"
                            onChange={handleLogoChange}
                          />
                          <label htmlFor="logo-upload">
                            <Button
                              variant="outlined"
                              size="small"
                              component="span"
                              startIcon={<CloudUploadIcon />}
                              disabled={uploadingLogo}
                              sx={{ bgcolor: '#fff' }}
                            >
                              {logoFile ? 'Change file' : settings.logoUrl ? 'Replace' : 'Upload logo'}
                            </Button>
                          </label>
                          {logoFile && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleLogoUpload}
                              disabled={uploadingLogo}
                              startIcon={uploadingLogo ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                            >
                              {uploadingLogo ? 'Uploading…' : 'Apply'}
                            </Button>
                          )}
                          {(settings.logoUrl || logoFile) && (
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              onClick={handleRemoveLogo}
                              startIcon={<DeleteIcon />}
                            >
                              Remove
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography sx={sectionTitleSx}>Order numbering</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Order prefix"
                          value={settings.orderPrefix || ''}
                          onChange={handleInputChange('orderPrefix')}
                          placeholder="e.g. ORD"
                          helperText="Max 10 characters"
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Order number reset</InputLabel>
                          <Select
                            value={settings.orderResetFrequency || 'MONTHLY'}
                            onChange={handleInputChange('orderResetFrequency')}
                            label="Order number reset"
                          >
                            <MenuItem value="DAILY">Daily</MenuItem>
                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                            <MenuItem value="YEARLY">Yearly</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography sx={sectionTitleSx}>New product SKUs</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="SKU prefix"
                          value={settings.skuPrefix || ''}
                          onChange={handleInputChange('skuPrefix')}
                          placeholder="e.g. GTR"
                          helperText="Leave blank for legacy auto SKU"
                          inputProps={{ maxLength: 20 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Number pattern"
                          value={settings.skuPattern || '001'}
                          onChange={handleInputChange('skuPattern')}
                          placeholder="001"
                          helperText="001 → 3 digits, 0001 → 4 digits"
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>
                      {(settings.skuPrefix || '').trim() ? (
                        <Grid item xs={12}>
                          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: PANEL_BORDER }}>
                            <Typography variant="caption" color="text.secondary">
                              Example: {settings.skuPrefix}001, {settings.skuPrefix}002, {settings.skuPrefix}003…
                            </Typography>
                          </Box>
                        </Grid>
                      ) : null}
                    </Grid>
                  </Box>
                </Stack>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography sx={sectionTitleSx}>Features</Typography>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={settings.dayCloseEnabled || false}
                            onChange={handleToggleChange('dayCloseEnabled')}
                            color="primary"
                          />
                        }
                        label={<Typography variant="body2">Day begin / day close</Typography>}
                        sx={{ ml: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={settings.enableBatchWise || false}
                            onChange={handleToggleChange('enableBatchWise')}
                            color="primary"
                          />
                        }
                        label={<Typography variant="body2">Batch &amp; expiry tracking</Typography>}
                        sx={{ ml: 0 }}
                      />
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography sx={sectionTitleSx}>Stock &amp; profit</Typography>
                    <FormControl fullWidth size="small" sx={{ maxWidth: { md: 400 } }}>
                      <InputLabel id="stock-profit-cost-method-label">Profit cost basis</InputLabel>
                      <Select
                        labelId="stock-profit-cost-method-label"
                        label="Profit cost basis"
                        value={settings.stockProfitCostMethod || 'FIFO'}
                        onChange={(e) => setSettings((prev) => ({ ...prev, stockProfitCostMethod: e.target.value }))}
                      >
                        <MenuItem value="FIFO">FIFO</MenuItem>
                        <MenuItem value="WEIGHTED_AVERAGE">Weighted average</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
                      Affects stock reports and invoice P&amp;L. Other navigation options follow batch and day-close flags.
                    </Typography>
                  </Box>
                </Stack>
              </TabPanel>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Layout>
  );
};

export default CompanySettings; 
